/**
 * Bespoke schematic glyph set — the ONE path source (HEX-102, ADR-0020 §3).
 *
 * Ports the designer's `Glyph` component
 * (`design-handoff/.../mockups/js/primitives.jsx`) into a typed, renderer-
 * agnostic op model. Both render surfaces read this single source so the visual
 * language can never drift:
 *   - DOM SVG  (`domGlyph.ts`)        — UI chrome; colour via `currentColor`.
 *   - Pixi `Graphics` (`pixi/glyphGraphics.ts`) — overworld tiles / markers.
 *
 * Replaces the unicode-codepoint glyph map (pre-HEX-102 `tileGlyphs.ts` drew
 * BMP characters as Pixi `Text`). Geometry only — colour, stroke width and
 * placement are the consuming surface's concern, applied at render time.
 *
 * Every glyph lives in a 24×24 viewBox. Each op is either `stroke` (open line
 * art: `fill:none`, round caps/joins) or `fill` (solid accents — arrowheads,
 * pips, eyes), mirroring the handoff's `p` / `fp` prop split.
 */

/** Glyph viewBox edge — every op's coordinates are in `[0, 24]`. */
export const GLYPH_VIEWBOX = 24;

/** Default stroke width (handoff `sw`), in viewBox units. */
export const DEFAULT_GLYPH_STROKE_WIDTH = 1.7;

/** How an op paints: an open stroked line, or a solid filled shape. */
export type GlyphOpMode = 'stroke' | 'fill';

/** A single drawable primitive within a glyph. */
export type GlyphOp =
  | { readonly t: 'path'; readonly d: string; readonly mode: GlyphOpMode }
  | {
      readonly t: 'circle';
      readonly cx: number;
      readonly cy: number;
      readonly r: number;
      readonly mode: GlyphOpMode;
    }
  | {
      readonly t: 'rect';
      readonly x: number;
      readonly y: number;
      readonly w: number;
      readonly h: number;
      readonly rx: number;
      readonly mode: GlyphOpMode;
    };

/** Every glyph name in the set (card types · biomes · world content · utility). */
export type GlyphName =
  // card types
  | 'attack'
  | 'defense'
  | 'support'
  | 'recursive'
  | 'trap'
  // biomes
  | 'plain'
  | 'forest'
  | 'snow'
  | 'lake'
  | 'mountain'
  // world content
  | 'combat'
  | 'chest'
  | 'merchant'
  | 'town'
  | 'pillar'
  | 'shop'
  | 'trapMark'
  | 'home'
  // utility
  | 'die'
  | 'eye'
  | 'skull';

// Op constructors — keep the table below as close to the JSX source as possible.
const s = (d: string): GlyphOp => ({ t: 'path', d, mode: 'stroke' });
const f = (d: string): GlyphOp => ({ t: 'path', d, mode: 'fill' });
const sc = (cx: number, cy: number, r: number): GlyphOp => ({
  t: 'circle',
  cx,
  cy,
  r,
  mode: 'stroke',
});
const fc = (cx: number, cy: number, r: number): GlyphOp => ({
  t: 'circle',
  cx,
  cy,
  r,
  mode: 'fill',
});
const sr = (x: number, y: number, w: number, h: number, rx: number): GlyphOp => ({
  t: 'rect',
  x,
  y,
  w,
  h,
  rx,
  mode: 'stroke',
});

/**
 * The glyph table — one entry per `GlyphName`, transcribed verbatim from the
 * handoff `Glyph` component's path data (`primitives.jsx`).
 */
export const GLYPHS: Readonly<Record<GlyphName, readonly GlyphOp[]>> = {
  // ── card types ──────────────────────────────────────────────────────────
  attack: [s('M5 19 L15 7'), s('M12 5 L19 5 L19 12'), s('M13 16 L17 19')],
  defense: [s('M12 3 L19 6 V12 C19 16 16 19 12 21 C8 19 5 16 5 12 V6 Z'), s('M12 8 V15')],
  support: [s('M12 5 V19'), s('M5 12 H19'), sc(12, 12, 6.5)],
  recursive: [s('M7 9 A6 6 0 1 1 6 14'), f('M7 5 L7.5 10 L3 8.5 Z')],
  trap: [s('M12 4 L20 19 H4 Z'), s('M12 10 V14'), fc(12, 16.6, 1)],
  // ── biomes ──────────────────────────────────────────────────────────────
  plain: [s('M4 16 H20'), s('M8 16 V12'), s('M12 16 V10'), s('M16 16 V12.5')],
  forest: [s('M12 4 L16 12 H8 Z'), s('M12 9 L17 17 H7 Z'), s('M12 17 V20')],
  snow: [s('M12 3 V21'), s('M3.7 7.5 L20.3 16.5'), s('M20.3 7.5 L3.7 16.5')],
  lake: [s('M4 9 C7 7 9 11 12 9 C15 7 17 11 20 9'), s('M4 14 C7 12 9 16 12 14 C15 12 17 16 20 14')],
  mountain: [s('M3 19 L10 7 L14 13 L17 9 L21 19 Z'), s('M8 10.5 L11.5 13')],
  // ── world content ─────────────────────────────────────────────────────────
  combat: [
    s('M5 5 L19 19'),
    s('M19 5 L5 19'),
    f('M16.5 4 L20 4 L20 7.5 Z'),
    f('M7.5 20 L4 20 L4 16.5 Z'),
  ],
  chest: [
    sr(4.5, 9, 15, 10, 1.5),
    s('M4.5 13 H19.5'),
    s('M4.5 9 C4.5 6 19.5 6 19.5 9'),
    f('M11 12 h2 v3 h-2 z'),
  ],
  merchant: [
    sc(12, 12, 8),
    s('M12 7.5 V16.5'),
    s('M14.5 9 C14.5 7.5 9.5 7.5 9.5 10 C9.5 12.5 14.5 12 14.5 14.5 C14.5 17 9.5 17 9.5 15'),
  ],
  town: [s('M5 20 V11 L9 8 L9 11 L12 8 L12 11 L15 8 L19 11 V20 Z'), s('M10 20 V15 H14 V20')],
  pillar: [
    s('M12 3 L15 8 L12 11 L9 8 Z'),
    s('M10.5 11 L9.5 20'),
    s('M13.5 11 L14.5 20'),
    s('M8 20 H16'),
  ],
  shop: [
    s('M5 9 L7 5 H17 L19 9'),
    s('M5 9 H19 V13 C19 13 17 14 12 14 C7 14 5 13 5 13 Z'),
    s('M6.5 14 V20 H17.5 V14'),
  ],
  trapMark: [s('M12 3 L21 20 H3 Z'), s('M12 9 V14'), fc(12, 17, 1.1)],
  home: [s('M4 11 L12 4 L20 11'), s('M6 10 V20 H18 V10'), s('M10 20 V14 H14 V20')],
  // ── utility ─────────────────────────────────────────────────────────────
  die: [sr(4.5, 4.5, 15, 15, 3), fc(9, 9, 1.3), fc(15, 15, 1.3), fc(12, 12, 1.3)],
  eye: [s('M3 12 C6 6 18 6 21 12 C18 18 6 18 3 12 Z'), sc(12, 12, 3)],
  skull: [
    s('M5 11 C5 6 19 6 19 11 V14 L17 16 V19 H7 V16 L5 14 Z'),
    fc(9.5, 12, 1.4),
    fc(14.5, 12, 1.4),
  ],
};

/** All glyph names, for iteration / exhaustiveness checks. */
export const GLYPH_NAMES = Object.keys(GLYPHS) as GlyphName[];

/**
 * Type guard: is `name` a glyph the set actually carries? (HEX-109.) The render
 * path can receive a name the map does not cover — e.g. a legacy `TileType`
 * persisted before its glyph existed surfaces as `undefined` through the
 * non-exhaustive `TILE_GLYPH` lookup. Callers gate on this so an unmapped name
 * degrades to "no glyph" rather than indexing `GLYPHS[undefined]` and throwing.
 */
export function isGlyphName(name: string | null | undefined): name is GlyphName {
  return typeof name === 'string' && Object.prototype.hasOwnProperty.call(GLYPHS, name);
}

/** Colour / stroke styling for the SVG-string builders. */
export interface GlyphSvgStyle {
  /** Colour for `stroke`-mode ops. Default `currentColor`. */
  readonly stroke?: string;
  /** Colour for `fill`-mode ops. Default `currentColor`. */
  readonly fill?: string;
  /** Stroke width in viewBox units. Default `DEFAULT_GLYPH_STROKE_WIDTH`. */
  readonly strokeWidth?: number;
}

function opToSvg(op: GlyphOp, style: Required<GlyphSvgStyle>): string {
  const attrs =
    op.mode === 'stroke'
      ? `fill="none" stroke="${style.stroke}" stroke-width="${style.strokeWidth}" stroke-linecap="round" stroke-linejoin="round"`
      : `fill="${style.fill}" stroke="none"`;
  switch (op.t) {
    case 'path':
      return `<path d="${op.d}" ${attrs}/>`;
    case 'circle':
      return `<circle cx="${op.cx}" cy="${op.cy}" r="${op.r}" ${attrs}/>`;
    case 'rect':
      return `<rect x="${op.x}" y="${op.y}" width="${op.w}" height="${op.h}" rx="${op.rx}" ${attrs}/>`;
  }
}

/**
 * The glyph's inner SVG markup (no `<svg>` wrapper) — the shape elements only.
 * Lets callers compose multiple passes into one document (e.g. the Pixi drawer's
 * dark-halo + light-ink stack).
 */
export function glyphInnerSvg(name: GlyphName, style: GlyphSvgStyle = {}): string {
  // Defensive (HEX-109): an unmapped name yields empty markup rather than a
  // `GLYPHS[undefined].map` TypeError. The render path treats "" as no overlay.
  const ops = GLYPHS[name];
  if (!ops) return '';
  const resolved: Required<GlyphSvgStyle> = {
    stroke: style.stroke ?? 'currentColor',
    fill: style.fill ?? 'currentColor',
    strokeWidth: style.strokeWidth ?? DEFAULT_GLYPH_STROKE_WIDTH,
  };
  return ops.map((op) => opToSvg(op, resolved)).join('');
}

/** A self-contained `<svg>` document for `name`, sized to the 24×24 viewBox. */
export function glyphToSvg(name: GlyphName, style: GlyphSvgStyle = {}): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${GLYPH_VIEWBOX} ${GLYPH_VIEWBOX}">${glyphInnerSvg(
    name,
    style,
  )}</svg>`;
}
